import { isNil } from 'lodash';
import { FinancialStatementPeriodFullWrite, ValuationApproachGPC } from 'api';

type StatementPeriod = Pick<FinancialStatementPeriodFullWrite, 'id' | 'financial_statement' | 'statement_date'> & {
  period_type?: number | null;
};

const PERIOD_SELECTION_FIELDS = [
  'ltm_financial_statement_period_id',
  'ntm_financial_statement_period_id',
  'first_period_selection',
  'second_period_selection',
  'first_period_forward_selection',
  'second_period_forward_selection',
] as const;

interface RemapGPCPeriodsByVersionParams {
  gpc: ValuationApproachGPC;
  periods: StatementPeriod[];
  fromStatementId: number;
  toStatementId: number;
}

const periodKey = ({ statement_date, period_type }: StatementPeriod) =>
  `${statement_date}|${period_type}`;

// Carries the public comps period selections over to the statement version the approach switches to,
// matching by statement date and period type, so the page computes the same fiscal years the backend stores.
const remapGPCPeriodsByVersion = (params: RemapGPCPeriodsByVersionParams): ValuationApproachGPC => {
  const { gpc, periods, fromStatementId, toStatementId } = params;

  if (fromStatementId === toStatementId) {
    return gpc;
  }

  const periodsById = new Map(periods.map(period => [period.id, period]));
  const targetIdsByKey = new Map(
    periods.filter(period => period.financial_statement === toStatementId).map(period => [periodKey(period), period.id])
  );

  const remapId = (id?: number | null) => {
    const period = periodsById.get(Number(id));

    if (period?.financial_statement === toStatementId) {
      return id;
    }

    return period?.financial_statement === fromStatementId ? targetIdsByKey.get(periodKey(period)) ?? null : null;
  };

  const selectedFields = PERIOD_SELECTION_FIELDS.filter(field => !isNil(gpc[field]));

  return { ...gpc, ...Object.fromEntries(selectedFields.map(field => [field, remapId(gpc[field])])) };
};

export default remapGPCPeriodsByVersion;
